<script lang="ts">
  import { formatPriceDetailed, nounForPropertyTypeSlug } from "$lib/utils";
  import * as Plot from "@observablehq/plot";
  import * as htl from "htl";
  import { innerWidth } from "svelte/reactivity/window";
  import { format, subYears } from "date-fns";
  import type { PageProps } from "./$types";
  import BasicPageMetadata from "$lib/BasicPageMetadata.svelte";
  import { cn } from "@carto-au/svelte";
  import DateDisplay from "$lib/DateDisplay.svelte";

  let { data, params }: PageProps = $props();

  let currentPoint = $derived(data.chartPoints[data.chartPoints.length - 1]);

  let rangeYears = $state<number | null>(null);

  let chartPoints = $derived(
    rangeYears === null
      ? data.chartPoints
      : data.chartPoints.filter(
          (d) => d.Date >= subYears(currentPoint.Date, rangeYears!),
        ),
  );

  const btnClass =
    "cursor-pointer rounded-md bg-stone-200 px-3 py-0.5 text-sm font-semibold uppercase";

  // Same as app.css
  const FONT_FAMILY = `
    "Libre Franklin Variable",
    -apple-system,
    BlinkMacSystemFont,
    Segoe UI,
    Roboto,
    Oxygen,
    Ubuntu,
    Cantarell,
    Open Sans,
    Helvetica Neue,
    sans-serif`;

  let div: HTMLElement | undefined = $state();

  let detailedMode = $derived(rangeYears ?? Infinity <= 5);

  $effect(() => {
    const sorted = chartPoints.map((p) => p.Median).sort((a, b) => a - b);
    const minMax = [sorted[0], sorted[sorted.length - 1]];

    div?.firstChild?.remove(); // remove old chart, if any
    div?.append(
      Plot.plot({
        height: 400,
        width: innerWidth.current,
        margin: 40,
        marginTop: 10,
        y: {
          domain: detailedMode ? minMax : undefined,
        },
        marks: [
          Plot.ruleY([0]),
          Plot.axisY({
            // x: (y) => chartPoints.find((d) => d.Median >= y)?.Date,
            // insetLeft: -6,
            textStroke: "white",
            tickFormat: new Intl.NumberFormat("en", {
              notation: "compact",
              minimumSignificantDigits: detailedMode ? 3 : undefined,
            }).format,
            tickSize: 0,
            fontFamily: FONT_FAMILY,
            fontSize: 11,
            label: null,
          }),
          Plot.axisX({
            textStroke: "white",
            fontFamily: FONT_FAMILY,
            fontSize: 11,
            tickSpacing: 50,
          }),
          Plot.gridY({
            // x: (y) => chartPoints.find((d) => d.Median >= y)?.Date,
            // insetLeft: -6,
            // strokeDasharray: "0.75,2", // dashed
            // strokeOpacity: 1, // opaque
          }),
          detailedMode
            ? undefined
            : Plot.areaY(chartPoints, {
                x: "Date",
                y: "Median",
                fill: "hsla(0, 40%, 93%,1)",
              }),
          Plot.lineY(chartPoints, {
            x: "Date",
            y: "Median",
            strokeWidth: 2.5,
            stroke: detailedMode ? "#c74d88" : "url(#gradient-line-y)",
            // tip: true,
          }),
          // Plot.tip(
          //   chartPoints,
          //   Plot.pointerX({
          //     x: "Date",
          //     y: "Median",
          //     fontSize: 20,
          //     fontFamily: FONT_FAMILY,
          //   }),
          // ),
          // Plot.crosshairX(chartPoints, { x: "Date", y: "Median" }),
          Plot.ruleX(
            chartPoints,
            Plot.pointerX({ x: "Date", py: "Median", opacity: 0.2 }),
          ),
          Plot.dot(
            chartPoints,
            Plot.pointerX({
              x: "Date",
              y: "Median",
              fill: true,
              r: 5,
              stroke: "white",
              strokeWidth: 2,
            }),
          ),
          Plot.text(
            chartPoints,
            Plot.pointerX({
              x: "Date",
              y: "Median",
              text: (d) => formatPriceDetailed(d.Median),
              fontWeight: "bold",
              fontFamily: FONT_FAMILY,
              fontSize: 16,
              lineAnchor: "bottom",
              fill: "currentColor",
              stroke: "white",
              strokeWidth: 4,
              dy: 25,
            }),
          ),
          Plot.text(
            chartPoints,
            Plot.pointerX({
              x: "Date",
              y: "Median",
              text: (d) => format(d.Date, "d MMM yyyy"),
              fontWeight: "bold",
              fontFamily: FONT_FAMILY,
              fontSize: 12,
              lineAnchor: "bottom",
              fill: "currentColor",
              stroke: "white",
              strokeWidth: 4,
              dy: 41,
            }),
          ),
          () => htl.svg`
          <linearGradient gradientUnits="userSpaceOnUse" x1="0" x2="0" y1="0" id="gradient-line-y" y2="400"><stop stop-color="#30133a" offset="-5.646644844517185"></stop><stop stop-color="#5d336b" offset="-1.2822149481723952"></stop><stop stop-color="#c74d88" offset="-0.19110747408619752"></stop><stop stop-color="#e5a50a" offset="0.9"></stop></linearGradient>
          `,
        ],
      }),
    ); // add the new chart
  });
</script>

<svelte:head>
  <BasicPageMetadata
    title={`Sydney median ${nounForPropertyTypeSlug(
      params.propertyTypeSlug,
    )} prices chart (interactive) — 2002-2026`}
    description={`An interactive chart showing the median ${nounForPropertyTypeSlug(
      params.propertyTypeSlug,
    )} price in Sydney over time from 2002 to 2026, updated weekly.`}
  />
</svelte:head>

<div class="p-6 pt-0 pb-0 text-center">
  <h1 class="text-2xl font-semibold text-balance text-pretty sm:text-3xl">
    Sydney median {nounForPropertyTypeSlug(params.propertyTypeSlug)} price chart
    &mdash; 2002&minus;2026
  </h1>
  <p class="mt-1">
    The chart below shows the median {nounForPropertyTypeSlug(
      params.propertyTypeSlug,
    )} price across all of Sydney over the past 24 years to <DateDisplay
      class="bg-stone-200 px-0.5 font-semibold whitespace-nowrap"
      date={currentPoint.Date}
    />.
  </p>
  <div class="mt-4 text-center text-lg font-medium">
    Current median price:&nbsp;&nbsp;
    <span class="text-lg/tight font-bold">
      {formatPriceDetailed(currentPoint.Median)}
    </span>
  </div>
</div>

<div class="mt-4 mb-4 flex items-center justify-center gap-2">
  <button
    class={cn(btnClass, rangeYears === null && "bg-stone-300")}
    onclick={() => {
      rangeYears = null;
    }}
  >
    All time
  </button>
  <button
    class={cn(btnClass, rangeYears === 5 && "bg-stone-300")}
    onclick={() => {
      rangeYears = 5;
    }}
  >
    5 years
  </button>
  <button
    class={cn(btnClass, rangeYears === 1 && "bg-stone-300")}
    onclick={() => {
      rangeYears = 1;
    }}
  >
    1 year
    <!-- <div
      class="-mr-1.5 ml-1 inline-block rounded bg-green-300 px-1 py-0.5 text-xs font-bold"
    >
      NEW
    </div> -->
  </button>
</div>

<div class="pl-6 select-none">
  <div class="overflow-hidden">
    <div bind:this={div} role="img" class="h-[400px]"></div>
  </div>
</div>
<div class="p-9 pt-0">
  <div class="rounded-lg bg-stone-100 p-4">
    <span class="bg-stone-200 px-0.5 font-semibold">How we calculate this:</span
    > We use a stratified median similar to the Australian Bureau of Statistics,
    Domain House Price Report, and the Reserve Bank of Australia. We group suburbs
    into 'buckets' based on their median price over the past 5 years. Each point
    on the graph represents the median bucket price based on sales over the 90 days
    prior to that date.
  </div>
</div>
